import { ApproveIcon, DeclineIcon, DropDownIcon } from '@/src/assets/svgs'
import { useRequisitions } from '@hook'
import { PHARMACY_MODAL_TYPE, useModalStore } from '@modal'
import { RequisitionData, RequisitionIncluded, RequisitionItem } from '@type/requisitions.type'
import { Badge, DropdownBtn, Loader, SideDrawer } from '@ui'
import { getIncludedData } from '@util'
import { CheckBox } from 'grommet'
import moment from 'moment'
import { useMemo, useState } from 'react'

type Props = {
  onClose: () => void
  requisitionId?: string
  defaultData?: RequisitionData
  included?: RequisitionIncluded[]
}

type DrawerTab = 'pending' | 'approved' | 'declined'

const normalizeItemStatus = (status?: string) => {
  const lower = status?.toLowerCase()
  if (lower === 'approved' || lower === 'partially_approved') return 'approved'
  if (lower === 'declined' || lower === 'rejected') return 'declined'
  return 'pending'
}

const statusLabel = (status?: string) => status?.replace(/_/g, ' ') || 'pending review'

const getRequisitionItems = (requisition?: RequisitionData, included: RequisitionIncluded[] = []) => {
  const attributesItems = requisition?.attributes.items
  if (attributesItems?.length) return attributesItems

  const itemRefs = requisition?.relationships?.requisition_items?.data || []

  return itemRefs
    .map(ref => getIncludedData(included, 'requisition_item', ref.id))
    .filter((item): item is RequisitionIncluded => Boolean(item))
    .map(item => ({ id: item.id, ...(item.attributes as RequisitionItem) }))
}

const itemLabel = (item: RequisitionItem) => {
  const brand = item.brand_name?.trim()
  const generic = item.generic_name?.trim()
  const qty = item.approved_quantity === '0.0' ? item.requested_quantity : item.approved_quantity

  const parts = [
    brand ? `BN: ${brand}` : '',
    generic ? `GN: ${generic}` : '',
    qty !== undefined && qty !== null && String(qty) !== '' ? `QTY: ${qty}` : ''
  ].filter(Boolean)

  return parts.length > 0 ? parts.join(', ') : 'Unnamed drug'
}

const tabLabels = {
  pending: 'Pending',
  approved: 'Approved',
  declined: 'Declined'
}

export const AssessRequisition = ({ onClose, requisitionId = '', defaultData, included = [] }: Props) => {
  const [drawerTab, setDrawerTab] = useState<DrawerTab>('pending')
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([])
  const { openModal } = useModalStore()

  const options: any = { id: requisitionId, defaultData, defaultIncluded: included }
  const { requisition, requisitionIncluded, requisitionIsLoading } = useRequisitions(options)

  const normalizedDrawerItems = useMemo(() => {
    const items = getRequisitionItems(requisition, requisitionIncluded)
    return items.map((item, index) => ({
      ...item,
      id: String(item.id || index)
    }))
  }, [requisition, requisitionIncluded])

  const drawerItemsByStatus = useMemo(
    () => ({
      approved: normalizedDrawerItems.filter(item => normalizeItemStatus(item.status) === 'approved'),
      declined: normalizedDrawerItems.filter(item => normalizeItemStatus(item.status) === 'declined'),
      pending: normalizedDrawerItems.filter(item => normalizeItemStatus(item.status) === 'pending')
    }),
    [normalizedDrawerItems]
  )

  const tabOptions = useMemo(
    () => [
      { label: `Pending (${drawerItemsByStatus.pending.length})`, value: 'pending' },
      { label: `Approved (${drawerItemsByStatus.approved.length})`, value: 'approved' },
      { label: `Declined (${drawerItemsByStatus.declined.length})`, value: 'declined' }
    ],
    [drawerItemsByStatus]
  )

  const visibleDrawerItems = useMemo(() => {
    if (drawerTab === 'approved') return drawerItemsByStatus.approved
    if (drawerTab === 'declined') return drawerItemsByStatus.declined
    return drawerItemsByStatus.pending
  }, [drawerItemsByStatus, drawerTab])

  const selectedPendingItems = useMemo(
    () => drawerItemsByStatus.pending.filter(item => selectedItemIds.includes(String(item.id))),
    [drawerItemsByStatus.pending, selectedItemIds]
  )

  const isAllVisibleSelected = useMemo(() => {
    if (!visibleDrawerItems.length) return false
    return visibleDrawerItems.every(item => selectedItemIds.includes(String(item.id)))
  }, [selectedItemIds, visibleDrawerItems])

  const toggleItemSelection = (id: string, checked: boolean) => {
    setSelectedItemIds(prev => {
      if (checked) return prev.includes(id) ? prev : [...prev, id]
      return prev.filter(itemId => itemId !== id)
    })
  }

  const toggleAllVisible = (checked: boolean) => {
    const visibleIds = visibleDrawerItems.map(item => String(item.id))
    setSelectedItemIds(prev => {
      if (checked) return Array.from(new Set([...prev, ...visibleIds]))
      return prev.filter(id => !visibleIds.includes(id))
    })
  }

  const selectedRequestedBy = requisition?.relationships?.requested_by?.data?.id
  const selectedReviewedBy = requisition?.relationships?.reviewed_by?.data?.id
  const selectedUnit = requisition?.relationships?.unit?.data?.id

  const selectedRequestedByName = (requisitionIncluded || []).find(
    item => item.type === 'staff' && item.id === selectedRequestedBy
  )?.attributes
  const selectedReviewedByName = (requisitionIncluded || []).find(
    item => item.type === 'staff' && item.id === selectedReviewedBy
  )?.attributes
  const selectedUnitName = (requisitionIncluded || []).find(
    item => item.type === 'unit' && item.id === selectedUnit
  )?.attributes

  const itemCount = normalizedDrawerItems.length

  const header = (
    <div className="px-5 pb-4 flex flex-col gap-4 border-b border-[#D9D9D9]">
      <div className="pt-4">
        <Badge
          label={statusLabel(requisition?.attributes.status)}
          size="small"
          status={requisition?.attributes.status}
        />
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <p>
          Requested by:{' '}
          <span className="font-medium">
            {selectedRequestedByName
              ? `${selectedRequestedByName.first_name || ''} ${selectedRequestedByName.last_name || ''}`
              : '-'}
          </span>
        </p>
        <p>
          Unit: <span className="font-medium">{selectedUnitName?.name || '-'}</span>
        </p>
        <p>
          Date:{' '}
          <span className="font-medium">
            {requisition?.attributes.created_at ? moment(requisition.attributes.created_at).format('DD/MM/YYYY') : '-'}
          </span>
        </p>
        <p>
          Reviewed by:{' '}
          <span className="font-medium">
            {selectedReviewedByName
              ? `${selectedReviewedByName.first_name || ''} ${selectedReviewedByName.last_name || ''}`
              : '-'}
          </span>
        </p>
        <p>
          No. of items requested: <span className="font-medium">{itemCount ? itemCount : '-'}</span>
        </p>
      </div>
    </div>
  )

  return (
    <SideDrawer
      header={header}
      onClose={onClose}
      options={tabOptions.map(option => option.label)}
      setTab={(tabLabel: string) => {
        const option = tabOptions.find(o => o.label === tabLabel)
        if (option) setDrawerTab(option.value as DrawerTab)
      }}
      title="Assess Request"
    >
      <div className="p-5 h-full overflow-auto">
        {visibleDrawerItems.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center">
            <p className="text-2xl font-semibold">No {tabLabels[drawerTab]} requisitions</p>
            <p className="text-sm text-[#666] mt-1">Your {tabLabels[drawerTab]} requisitions will appear here</p>
          </div>
        ) : (
          <div className="flex flex-col">
            {drawerTab === 'pending' ? (
              <div className="flex items-center justify-between mb-4">
                <label className="flex items-center gap-2 py-2 border-b border-[#F2F2F2]">
                  <CheckBox checked={isAllVisibleSelected} onChange={event => toggleAllVisible(event.target.checked)} />
                  <span className="text-sm font-medium">All Drugs</span>
                </label>

                {selectedItemIds.length > 0 && (
                  <DropdownBtn
                    dropdownLabel={
                      <div className="flex items-center gap-2">
                        <span>Action</span>
                        <DropDownIcon />
                      </div>
                    }
                    options={[
                      {
                        handleClick: close => {
                          const isAllSelected =
                            selectedPendingItems.length > 0 &&
                            selectedPendingItems.length === drawerItemsByStatus.pending.length

                          openModal(PHARMACY_MODAL_TYPE.REQUISITION_APPROVE_DRUGS, {
                            allPendingItems: drawerItemsByStatus.pending,
                            defaultData: requisition,
                            included: requisitionIncluded,
                            requisitionId,
                            selectedItems: isAllSelected ? drawerItemsByStatus.pending : selectedPendingItems
                          })
                          close()
                        },
                        label: 'Approve'
                      },
                      {
                        handleClick: close => {
                          openModal(PHARMACY_MODAL_TYPE.REQUISITION_DECLINE, {
                            requisitionId,
                            selectedItems: selectedPendingItems
                          })
                          close()
                        },
                        label: 'Decline'
                      }
                    ]}
                    style={{ border: '1px solid #CCCCCC', borderRadius: 999, padding: '4px 10px' }}
                  />
                )}
              </div>
            ) : null}

            {visibleDrawerItems.map(item => {
              const itemId = String(item.id)
              const checked = selectedItemIds.includes(itemId)

              return (
                <div className="flex items-center justify-between gap-3 py-2 border-b border-[#F7F7F7]" key={itemId}>
                  <label className="flex items-center gap-2 flex-1">
                    {drawerTab === 'pending' ? (
                      <CheckBox
                        checked={checked}
                        onChange={event => toggleItemSelection(itemId, event.target.checked)}
                      />
                    ) : null}
                    <span className="text-sm">{itemLabel(item)}</span>
                  </label>

                  {drawerTab === 'pending' ? (
                    <div className="flex items-center gap-3">
                      <DeclineIcon
                        className="cursor-pointer"
                        onClick={() => {
                          openModal(PHARMACY_MODAL_TYPE.REQUISITION_DECLINE, {
                            requisitionId,
                            selectedItems: [item]
                          })
                        }}
                        type="button"
                      />
                      <ApproveIcon
                        className="cursor-pointer"
                        onClick={() => {
                          openModal(PHARMACY_MODAL_TYPE.REQUISITION_APPROVE_DRUGS, {
                            allPendingItems: drawerItemsByStatus.pending,
                            defaultData: requisition,
                            included: requisitionIncluded,
                            requisitionId,
                            selectedItems: [item]
                          })
                        }}
                        type="button"
                      />
                    </div>
                  ) : (
                    <Badge label={statusLabel(item.status)} size="small" status={item.status} />
                  )}
                </div>
              )
            })}
          </div>
        )}
        {requisitionIsLoading && (
          <div className="flex items-center justify-center h-full">
            <Loader />
          </div>
        )}
      </div>
    </SideDrawer>
  )
}
